import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, shareReplay, catchError, retry, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  PagedResult, Plan, Product, BlogPostList, BlogPostDetail, BlogCategory,
  GalleryAlbumSummary, GalleryAlbum, Job, Offer, Service, TeamMember, Testimonial, Faq, SiteSettingsMap, ContactRequest,
  Video, Client, SearchResult, UnsubscribeResult, SeoOverride, ReviewRequest
} from './cms.models';

@Injectable({ providedIn: 'root' })
export class CmsService {
  private base = environment.apiBaseUrl + '/public';

  /** In-memory GET cache: key -> { expires, shared observable }. Cleared on reload. */
  private cache = new Map<string, { exp: number; obs: Observable<unknown> }>();
  private readonly ttlMs = 5 * 60 * 1000; // 5 minutes

  constructor(private http: HttpClient) {}

  /**
   * Returns a cached, shared observable for `key` if still fresh; otherwise runs
   * `factory()`, shares its single emission, and caches it for the TTL. On error
   * the entry is evicted so the next call retries instead of replaying the failure.
   * Pass `refresh: true` to bypass any cached value and fetch fresh (then re-cache).
   */
  private cached<T>(key: string, factory: () => Observable<T>, refresh = false): Observable<T> {
    if (!refresh) {
      const hit = this.cache.get(key);
      if (hit && hit.exp > Date.now()) return hit.obs as Observable<T>;
    }

    const obs = factory().pipe(
      // A hanging request (slow server / cold app) would leave the page stuck on its
      // loading state forever (white screen until manual refresh). Fail fast after 12s so
      // the retry below can issue a fresh request; after the retries it surfaces the
      // page's error/empty state instead of an infinite blank.
      timeout(12000),
      // Tolerate a cold/slow API (MonsterASP) — retry a few times so first-load data
      // self-heals instead of leaving the page blank.
      retry({ count: 4, delay: 2000 }),
      catchError((err) => { this.cache.delete(key); return throwError(() => err); }),
      shareReplay(1)
    );
    this.cache.set(key, { exp: Date.now() + this.ttlMs, obs });
    return obs as Observable<T>;
  }

  /** Drops all cached responses (e.g. after a known content change). */
  clearCache(): void { this.cache.clear(); }

  /** Evicts cached entries whose key starts with `prefix` (e.g. 'blog'). */
  invalidate(prefix: string): void {
    for (const k of this.cache.keys()) if (k.startsWith(prefix)) this.cache.delete(k);
  }

  /** Turns a stored media URL (possibly relative) into a full URL. */
  mediaUrl(url?: string): string {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const origin = environment.apiBaseUrl.replace(/\/api$/, '');
    return origin + url;
  }

  getPlans(): Observable<Plan[]> {
    return this.cached('plans', () => this.http.get<Plan[]>(`${this.base}/plans`));
  }

  /** Active products with their plans (grouped) for the pricing page. */
  getProducts(): Observable<Product[]> {
    return this.cached('products', () => this.http.get<Product[]>(`${this.base}/products`));
  }

  getBlogPosts(page = 1, pageSize = 9, categorySlug?: string, search?: string, tag?: string): Observable<PagedResult<BlogPostList>> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (categorySlug) params = params.set('categorySlug', categorySlug);
    if (search) params = params.set('search', search);
    if (tag) params = params.set('tag', tag);
    return this.cached(`blog:${page}:${pageSize}:${categorySlug ?? ''}:${search ?? ''}:${tag ?? ''}`,
      () => this.http.get<PagedResult<BlogPostList>>(`${this.base}/blog`, { params }));
  }
  getBlogPost(slug: string): Observable<BlogPostDetail> {
    return this.cached(`blog:${slug}`, () => this.http.get<BlogPostDetail>(`${this.base}/blog/${slug}`));
  }
  getBlogCategories(): Observable<BlogCategory[]> {
    return this.cached('blog-categories', () => this.http.get<BlogCategory[]>(`${this.base}/blog/categories`));
  }

  getAlbums(): Observable<GalleryAlbumSummary[]> {
    return this.cached('albums', () => this.http.get<GalleryAlbumSummary[]>(`${this.base}/gallery`));
  }
  getAlbum(slug: string): Observable<GalleryAlbum> {
    return this.cached(`album:${slug}`, () => this.http.get<GalleryAlbum>(`${this.base}/gallery/${slug}`));
  }

  getJobs(page = 1, pageSize = 50, search?: string): Observable<PagedResult<Job>> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (search) params = params.set('search', search);
    return this.cached(`jobs:${page}:${pageSize}:${search ?? ''}`,
      () => this.http.get<PagedResult<Job>>(`${this.base}/jobs`, { params }));
  }
  getJob(slug: string): Observable<Job> {
    return this.cached(`job:${slug}`, () => this.http.get<Job>(`${this.base}/jobs/${slug}`));
  }

  getOffers(page = 1, pageSize = 50): Observable<PagedResult<Offer>> {
    const params = new HttpParams().set('page', page).set('pageSize', pageSize);
    return this.cached(`offers:${page}:${pageSize}`,
      () => this.http.get<PagedResult<Offer>>(`${this.base}/offers`, { params }));
  }

  getServices(): Observable<Service[]> {
    return this.cached('services', () => this.http.get<Service[]>(`${this.base}/services`));
  }
  getService(slug: string): Observable<Service> {
    return this.cached(`service:${slug}`, () => this.http.get<Service>(`${this.base}/services/${slug}`));
  }
  getTeam(): Observable<TeamMember[]> {
    return this.cached('team', () => this.http.get<TeamMember[]>(`${this.base}/team`));
  }
  getTestimonials(): Observable<Testimonial[]> {
    return this.cached('testimonials', () => this.http.get<Testimonial[]>(`${this.base}/testimonials`));
  }

  /** Public "leave a review" submission — lands in admin as pending until approved. */
  submitReview(body: ReviewRequest): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(`${this.base}/reviews`, body);
  }
  getFaqs(): Observable<Faq[]> {
    return this.cached('faqs', () => this.http.get<Faq[]>(`${this.base}/faqs`));
  }

  getVideos(): Observable<Video[]> {
    return this.cached('videos', () => this.http.get<Video[]>(`${this.base}/videos`));
  }
  getClients(): Observable<Client[]> {
    return this.cached('clients', () => this.http.get<Client[]>(`${this.base}/clients`));
  }

  /** Per-page SEO overrides (admin-managed). Cached; the app looks up by route path. */
  getSeoOverrides(): Observable<SeoOverride[]> {
    return this.cached('seo', () => this.http.get<SeoOverride[]>(`${this.base}/seo`));
  }

  /** Fetches site settings as a flat key -> value map (e.g. settings['contact.email']). */
  getSettings(): Observable<SiteSettingsMap> {
    return this.cached('settings', () =>
      this.http.get<{ key: string; value?: string }[]>(`${this.base}/settings`).pipe(
        map((list) => {
          const out: SiteSettingsMap = {};
          for (const s of list) out[s.key] = s.value ?? '';
          return out;
        })
      ));
  }

  submitContact(body: ContactRequest): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(`${this.base}/contact`, body);
  }

  /** Unified site search across blog posts, services, jobs and FAQs. Not cached (queries vary). */
  search(q: string, perType = 5): Observable<SearchResult[]> {
    const params = new HttpParams().set('q', q).set('perType', perType);
    return this.http.get<SearchResult[]>(`${this.base}/search`, { params });
  }

  /** Newsletter signup. `source` attributes where it came from (e.g. 'footer').
      `website` is the honeypot — leave empty for real submissions. */
  subscribe(email: string, source?: string, website?: string): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(`${this.base}/subscribe`, { email, source, website });
  }

  /** Unsubscribe / resubscribe by the token embedded in campaign-email links. */
  unsubscribe(token: string): Observable<UnsubscribeResult> {
    return this.http.post<UnsubscribeResult>(`${this.base}/unsubscribe`, { token });
  }
  resubscribe(token: string): Observable<UnsubscribeResult> {
    return this.http.post<UnsubscribeResult>(`${this.base}/resubscribe`, { token });
  }
}